import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/** @jsx jsx */
import React, { useCallback, useMemo, useState } from 'react';
import { css, jsx } from '@emotion/core';
import { N0, N500, N700, R400, Y300 } from '@atlaskit/theme/colors';
import { gridSize as getGridSize } from '@atlaskit/theme/constants';
var gridSize = getGridSize();
var MAX_HEIGHT = gridSize * 6.5;
var iconStyles = css({
  display: 'flex',
  alignItems: 'center',
  flex: '0 0 auto',
  flexDirection: 'column',
  '@media screen and (forced-colors: active)': {
    fill: 'CanvasText',
    filter: 'grayscale(100%)'
  }
});
var overflowStyles = css({
  overflow: 'hidden'
});
var contentStyles = css({
  display: 'flex',
  margin: 'auto',
  padding: gridSize * 1.5,
  alignItems: 'center',
  justifyContent: 'start',
  color: 'currentColor',
  fontWeight: 500,
  textAlign: 'center',
  // eslint-disable-next-line @repo/internal/styles/no-nested-styles
  'a, a:visited, a:hover, a:focus, a:active': {
    color: 'currentColor',
    textDecoration: 'underline'
  }
});
var childrenTextStyles = css({
  padding: gridSize / 2,
  flex: '0 1 auto',
  alignSelf: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});
var containerStyles = css({
  maxHeight: MAX_HEIGHT
});
var appearanceStyles = {
  warning: {
    backgroundColor: "var(--ds-background-warning-bold, ".concat(Y300, ")"),
    color: "var(--ds-text-warning-inverse, ".concat(N700, ")")
  },
  error: {
    backgroundColor: "var(--ds-background-danger-bold, ".concat(R400, ")"),
    color: "var(--ds-text-inverse, ".concat(N0, ")")
  },
  announcement: {
    backgroundColor: "var(--ds-background-neutral-bold, ".concat(N500, ")"),
    color: "var(--ds-text-inverse, ".concat(N0, ")")
  }
};

var Banner = function Banner(_ref) {
  var _ref$appearance = _ref.appearance,
      appearance = _ref$appearance === void 0 ? 'warning' : _ref$appearance,
      children = _ref.children,
      icon = _ref.icon,
      _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
      innerRef = _ref.innerRef,
      testId = _ref.testId;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      maxHeight = _useState2[0],
      setMaxHeight = _useState2[1];

  var bannerRef = useCallback(function (ref) {
    if (!ref) {
      return;
    }

    if (innerRef) {
      innerRef(ref);
    }

    setMaxHeight(ref.clientHeight);
  }, [innerRef]);
  var accessibilityProps = useMemo(function () {
    var baseProps = {
      'aria-hidden': !isOpen,
      role: 'alert'
    };

    if (appearance === 'announcement') {
      return _objectSpread(_objectSpread({}, baseProps), {}, {
        'aria-label': 'announcement',
        tabIndex: 0,
        role: 'region'
      });
    }

    return baseProps;
  }, [isOpen, appearance]);
  return jsx("div", {
    css: overflowStyles,
    style: {
      maxHeight: "".concat(isOpen ? maxHeight : 0, "px")
    },
    "data-testid": testId
  }, jsx("div", _extends({
    css: [containerStyles],
    style: _objectSpread({}, appearanceStyles[appearance]),
    ref: bannerRef
  }, accessibilityProps), jsx("div", {
    css: [contentStyles]
  }, jsx("span", {
    css: iconStyles,
    style: {
      fill: appearanceStyles[appearance].backgroundColor
    }
  }, icon), jsx("span", {
    css: [childrenTextStyles]
  }, children))));
}; // eslint-disable-next-line @repo/internal/react/require-jsdoc


export default Banner;